import MarketTabSection from '../../../common/MarketTabSection';
import { useOrderDetails } from '../../OrderDetailsProvider';

import { CPLAT_API_ROUTES, orderQueries, useFetch } from '@/api';
import { useAppSelector } from '@/redux/store';
import type { CplatApiResponse } from '@/types/api';
import { useSuspenseQuery } from '@tanstack/react-query';

import LayoutTableController from '@/pages/orders/_layouts/LayoutTableController';
import DateRangeSelect from '../../../common/DateRangeSelect';
import { SearchFilter } from '../../../common/SearchFilter';
import OrderTableColumn from './OrderTableColumn';

import GlobalSpinner from '@/components/organisms/GlobalSpinner';
import { DEFAULT_PAGE_SIZE } from '@/constants/order';
import { usePage } from '@/contexts/table-page';
import { useSort } from '@/contexts/table-sort';
import { useGetMarketIdFromSeachParams } from '@/hooks/use-get-market-id-from-search-params';
import { LayoutStickySection, LayoutTables } from '@/pages/orders/_layouts/LayoutOrderContainer';
import { OrderSeachField } from '@/types/order-list';
import { Stores } from '@/types/stores';
import { formatISOString } from '@/utils/format-iso-string';
import { useState } from 'react';
import NotRegisteredState from '../../../common/NotRegisteredState';

export const Shipping = () => {
  const user = useAppSelector((state) => state.user);
  const [searchField, setSearchField] = useState<OrderSeachField>('ExternalProductInfo');
  const [searchKeyword, setSearchKeyword] = useState('');

  const {
    // 날짜 관련 상태와 함수
    startDate,
    endDate,
    handleStartDate,
    handleEndDate,
  } = useOrderDetails();

  const { storeId, marketId } = useGetMarketIdFromSeachParams();
  const { sort } = useSort();
  const { page, setPage } = usePage();

  const getOrderListQuery = useSuspenseQuery(
    orderQueries.getOrderList({
      cplatToken: user.CplatToken,
      dateFrom: formatISOString(startDate),
      dateTo: formatISOString(endDate),
      skip: page * DEFAULT_PAGE_SIZE,
      take: DEFAULT_PAGE_SIZE,
      statusTab: 'Shipping',
      ...(storeId && { storeId }),
      ...(marketId && { marketId: Number(marketId) }),
      ...(sort && { sortInfo: { sortColumn: sort.sortColumn, sortDirection: sort.sortDirection } }),
      ...(searchField &&
        searchKeyword && {
          searchField,
          searchKeyword,
        }),
    }),
  );

  const orderListData = getOrderListQuery.data.data[0].orders;
  const totalPages = getOrderListQuery.data?.data[0].pagination.totalPages;

  const { data: storeListData } = useFetch<CplatApiResponse<Stores[][]>>(
    CPLAT_API_ROUTES.getStores,
    {
      method: 'POST',
      params: { cplatToken: user.CplatToken },
      v3: true,
    },
  );

  if (getOrderListQuery.isLoading) return <GlobalSpinner />;

  return (
    <>
      {(storeListData?.data[0].length ?? 0) >= 1 ? (
        <>
          <LayoutStickySection>
            <MarketTabSection totalLength={orderListData.length ?? 0} />
          </LayoutStickySection>
          <LayoutTables isTableBorder>
            <LayoutTableController>
              <div className="flex gap-[20px]">
                <DateRangeSelect
                  startDate={startDate}
                  handleStartDate={handleStartDate}
                  endDate={endDate}
                  handleEndDate={handleEndDate}
                />
                <SearchFilter
                  searchField={searchField}
                  searchKeyword={searchKeyword}
                  setSearchKeyword={setSearchKeyword}
                  setSearchField={setSearchField}
                  onSearch={(keyword) => setSearchKeyword(keyword)}
                  onReset={() => setSearchKeyword('')}
                />
              </div>
            </LayoutTableController>
            <OrderTableColumn
              orderList={orderListData ?? []}
              page={page}
              onChangePage={setPage}
              totalPages={totalPages ?? 0}
            />
          </LayoutTables>
        </>
      ) : (
        <NotRegisteredState className="bg-white" />
      )}
    </>
  );
};
